import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  ACTIONS,
  Controls,
  EventData,
  LIFECYCLE,
  STATUS,
  Step,
  TooltipRenderProps,
  useJoyride
} from "react-joyride";
import * as s from "./styles";
import {
  StartTourOptions,
  Tour,
  TourContextValue,
  TourProviderProps,
  TourStepData
} from "./types";

// Selector for an element marked with `data-tour={name}`. Pages mark the
// elements their tour points at, and build step targets with this.
export const tourTarget = (name: string) => `[data-tour="${name}"]`;

const COLUMN_TARGET_ATTRIBUTE = "data-tour-column";

// Joyride highlights a single element, and a table column is not one. This
// target is a stand-in: an invisible box put into the column's header cell and
// stretched down to the end of the table, so it scrolls along with the table.
export const columnTarget =
  (headerCellSelector: string) => (): HTMLElement | null => {
    const headerCell = document.querySelector<HTMLElement>(headerCellSelector);
    const table = headerCell?.closest("table");

    if (!headerCell || !table) {
      return null;
    }

    if (getComputedStyle(headerCell).position === "static") {
      headerCell.style.position = "relative";
    }

    let box = headerCell.querySelector<HTMLElement>(
      `[${COLUMN_TARGET_ATTRIBUTE}]`
    );

    if (!box) {
      box = document.createElement("div");
      box.setAttribute(COLUMN_TARGET_ATTRIBUTE, "");
      Object.assign(box.style, {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        pointerEvents: "none"
      });
      headerCell.appendChild(box);
    }

    box.style.height = `${
      table.getBoundingClientRect().bottom -
      headerCell.getBoundingClientRect().top
    }px`;

    return box;
  };

const isTargetRendered = (target: Step["target"]): boolean => {
  if (typeof target === "string") {
    return Boolean(document.querySelector(target));
  }

  return typeof target === "function" ? Boolean(target()) : true;
};

const TourContext = createContext<TourContextValue>({
  startTour: () => undefined
});

export const useTour = (): TourContextValue => useContext(TourContext);

// the keys that move the tour on, see TourProvider
const NEXT_KEYS = ["Enter", "ArrowRight"];

const getStepData = (step?: Step | null): TourStepData =>
  (step?.data as TourStepData | undefined) || {};

const goBack = (controls: Controls, index: number, step?: Step | null) => {
  const { backSteps = 1, onBack } = getStepData(step);

  onBack?.();

  if (backSteps === 1) {
    controls.prev();
  } else {
    controls.go(Math.max(index - backSteps, 0));
  }
};

const TourTooltip: FC<TooltipRenderProps> = ({
  backProps,
  closeProps,
  controls,
  index,
  isLastStep,
  primaryProps,
  size,
  skipProps,
  step,
  tooltipProps
}: TooltipRenderProps) => (
  <s.Tooltip elevation={8} {...tooltipProps}>
    <s.CloseButton {...closeProps} size={"small"}>
      <CloseIcon fontSize={"small"} />
    </s.CloseButton>
    <s.Header>
      <s.IconBadge>
        <AutoAwesomeOutlinedIcon fontSize={"small"} />
      </s.IconBadge>
      {step.title && <s.Title variant={"subtitle1"}>{step.title}</s.Title>}
    </s.Header>
    <s.Content>
      <Typography variant={"body2"} color={"textSecondary"} component={"div"}>
        {step.content}
      </Typography>
    </s.Content>
    <s.Progress variant={"determinate"} value={((index + 1) / size) * 100} />
    <s.Footer>
      <Typography variant={"caption"} color={"textSecondary"}>
        {`${index + 1} of ${size}`}
      </Typography>
      <s.Actions>
        {!isLastStep && (
          <Button {...skipProps} size={"small"} color={"inherit"}>
            {"Skip"}
          </Button>
        )}
        {index > 0 && (
          <Button
            {...backProps}
            onClick={() => goBack(controls, index, step)}
            size={"small"}
          >
            {"Back"}
          </Button>
        )}
        {getStepData(step).advanceOnTargetClick ? (
          <s.ClickHint>
            <TouchAppOutlinedIcon fontSize={"small"} />
            <Typography variant={"caption"}>
              {"Click it to continue"}
            </Typography>
          </s.ClickHint>
        ) : (
          <s.PrimaryButton
            {...primaryProps}
            size={"small"}
            variant={"contained"}
          >
            {isLastStep ? "Done" : "Next"}
          </s.PrimaryButton>
        )}
      </s.Actions>
    </s.Footer>
  </s.Tooltip>
);

export const TourProvider: FC<TourProviderProps> = ({
  children
}: TourProviderProps) => {
  const theme = useTheme();
  const [steps, setSteps] = useState<Step[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [pendingTour, setPendingTour] = useState<Tour | null>(null);
  // kept in a ref, so the Joyride event handler is not rebuilt per tour
  const onFinish = useRef<StartTourOptions["onFinish"]>(undefined);

  const startTour = useCallback((tour: Tour, options?: StartTourOptions) => {
    // Joyride hands the focus back to the element focused before the tour on
    // every step change, for a moment. With the keyboard that is enough for
    // the tooltip of the button that started the tour to flash each time.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setIsRunning(false);
    setPendingTour(tour);
    onFinish.current = options?.onFinish;
  }, []);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setSteps([]);
    document
      .querySelectorAll(`[${COLUMN_TARGET_ATTRIBUTE}]`)
      .forEach((box) => box.remove());
  }, []);

  // Resolving steps in an effect means the DOM is already committed, so steps
  // pointing at elements the page does not render right now (e.g. a table
  // shown only once data exists) are dropped instead of stalling the tour.
  useEffect(() => {
    if (!pendingTour) {
      return;
    }

    setPendingTour(null);

    const visibleSteps = pendingTour.reduce<Step[]>((visible, step) => {
      const previousStepData = getStepData(visible[visible.length - 1]);
      const { isDeferred, centerIfTargetMissing } = getStepData(step);

      if (centerIfTargetMissing && !isTargetRendered(step.target)) {
        return [...visible, { ...step, target: "body", placement: "center" }];
      }

      const isVisible = isDeferred
        ? Boolean(
            previousStepData.advanceOnTargetClick || previousStepData.isDeferred
          )
        : isTargetRendered(step.target);

      return isVisible ? [...visible, step] : visible;
    }, []);

    setSteps(visibleSteps);
    setIsRunning(visibleSteps.length > 0);
  }, [pendingTour]);

  const handleEvent = useCallback(
    ({ action, status }: EventData) => {
      if (status === STATUS.FINISHED) {
        stopTour();

        // closing the last step with the X finishes the tour too
        if (action !== ACTIONS.CLOSE) {
          onFinish.current?.();
        }

        return;
      }

      if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED) {
        stopTour();
      }
    },
    [stopTour]
  );

  const value = useMemo(() => ({ startTour }), [startTour]);

  const {
    controls,
    state,
    step,
    Tour: JoyrideTour
  } = useJoyride({
    continuous: true,
    run: isRunning,
    steps,
    onEvent: handleEvent,
    tooltipComponent: TourTooltip,
    // Joyride keeps the step index when the tour is closed with the X button
    // and reuses it for the next run, so every run is pinned to the first step.
    initialStepIndex: 0,
    // the buttons carry these as their title, so the default "Last" would
    // pop up over the Done button
    locale: { last: "Done" },
    // A tall step next to a big target fits neither above nor below it, and
    // by default the tooltip is only shifted sideways, so its top ends up
    // cut off by the window. Shifting it vertically too keeps it on screen,
    // over the target's edge if there is no other room.
    floatingOptions: { shiftOptions: { crossAxis: true } },
    options: {
      arrowColor:
        theme.palette.mode === "light"
          ? theme.palette.secondary.main
          : theme.palette.primary.main,
      overlayColor: alpha(
        theme.palette.common.black,
        theme.palette.mode === "dark" ? 0.7 : 0.45
      ),
      skipBeacon: true,
      // Joyride scrolls to a step's target even when it is already in view.
      // Steps whose target can sit below the fold opt back in with
      // `skipScroll: false`.
      skipScroll: true,
      spotlightPadding: 8,
      spotlightRadius: 8,
      targetWaitTimeout: 5000,
      zIndex: theme.zIndex.modal + 1
    },
    styles: {
      spotlight: {
        stroke: theme.palette.primary.main,
        strokeWidth: 2
      }
    }
  });

  const stepTarget = typeof step?.target === "string" ? step.target : null;
  const isWaitingForTargetClick =
    getStepData(step).advanceOnTargetClick === true &&
    state.lifecycle === LIFECYCLE.TOOLTIP;

  // The user's own click on the target moves the tour on, right as the thing
  // the next step points at appears. The listener is delegated, so a click on
  // any element matching the target counts, not only the highlighted one.
  // It listens in the capture phase: by the time a click bubbles up to the
  // document, React has already re-rendered, and the clicked node (e.g. an
  // icon swapped on toggle) may be detached, so `closest` would find nothing.
  useEffect(() => {
    if (!isWaitingForTargetClick || !stepTarget) {
      return;
    }

    const handleClick = ({ target }: MouseEvent) => {
      if (target instanceof Element && target.closest(stepTarget)) {
        controls.next();
      }
    };

    // Enter (and ArrowRight) acts as a click on the target, the same way it
    // presses Next on the other steps. Typing in a field is left alone.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !NEXT_KEYS.includes(event.key) ||
        event.repeat ||
        (event.target instanceof HTMLElement &&
          event.target.closest(
            'input, textarea, select, [contenteditable="true"]'
          ))
      ) {
        return;
      }
      const target = document.querySelector<HTMLElement>(stepTarget);
      if (target) {
        event.preventDefault();
        event.stopPropagation();
        target.click();
      }
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [controls, isWaitingForTargetClick, stepTarget]);

  // Without the focus trap the focus stays in the page (e.g. in a form the
  // step points at), so Enter would submit that form instead of pressing Next.
  // Here it moves the tour on, unless it is pressed on the tooltip's own
  // buttons, which handle it themselves. With the focus trap Enter is left to
  // the Next button, which has the focus.
  // ArrowRight presses Next on every step with the button. Typing in a field
  // is left alone, except without the focus trap, the same as for ArrowLeft.
  const isFocusLeftInPage = step?.disableFocusTrap === true;
  const isNextShown =
    !getStepData(step).advanceOnTargetClick &&
    state.lifecycle === LIFECYCLE.TOOLTIP;

  useEffect(() => {
    if (!isNextShown) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!NEXT_KEYS.includes(event.key) || event.repeat) {
        return;
      }
      if (
        event.key === "Enter" &&
        (!isFocusLeftInPage ||
          (event.target instanceof Element &&
            event.target.closest('[role="alertdialog"]')))
      ) {
        return;
      }
      if (
        event.key === "ArrowRight" &&
        !isFocusLeftInPage &&
        event.target instanceof HTMLElement &&
        event.target.closest(
          'input, textarea, select, [contenteditable="true"]'
        )
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      controls.next();
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [controls, isFocusLeftInPage, isNextShown]);

  // ArrowLeft acts as the Back button, shown from the second step on. Typing
  // in a field is left alone, there the arrow moves the caret. A step without
  // the focus trap is the exception: a dialog it points at puts the focus in
  // its first field by itself, the same reason Enter presses Next there.
  const canGoBack = state.index > 0 && state.lifecycle === LIFECYCLE.TOOLTIP;

  useEffect(() => {
    if (!canGoBack) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "ArrowLeft" ||
        event.repeat ||
        (!isFocusLeftInPage &&
          event.target instanceof HTMLElement &&
          event.target.closest(
            'input, textarea, select, [contenteditable="true"]'
          ))
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      goBack(controls, state.index, step);
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canGoBack, controls, isFocusLeftInPage, state.index, step]);

  return (
    <TourContext.Provider value={value}>
      {JoyrideTour}
      {children}
    </TourContext.Provider>
  );
};
